/** The three resting heights of `PosUiSheet`. */
export type TSheetHeight = 'sm' | 'md' | 'lg';

export const SHEET_HEIGHTS: readonly TSheetHeight[] = ['sm', 'md', 'lg'];

/** Share of the stable window height of each resting height. */
export const SHEET_HEIGHT_FRACTIONS: Readonly<Record<TSheetHeight, number>> = {
  sm: 0.4,
  md: 0.6,
  lg: 0.92,
};

/** A drag released below this share of the stable height closes the sheet. */
export const SHEET_CLOSE_FRACTION = 0.2;

/** A pointer that moved less than this many px is a tap, not a drag. */
export const SHEET_DRAG_THRESHOLD_PX = 4;

export function isSheetHeight(value: unknown): value is TSheetHeight {
  return SHEET_HEIGHTS.some((height) => height === value);
}

/** The panel height in px for a resting height (0 while the height is unknown). */
export function sheetHeightPx(
  height: TSheetHeight,
  stableHeight: number
): number {
  if (!Number.isFinite(stableHeight) || stableHeight <= 0) return 0;
  return Math.round(stableHeight * SHEET_HEIGHT_FRACTIONS[height]);
}

/** The height while dragging: never below 0, never above the tallest resting height. */
export function clampSheetDrag(px: number, stableHeight: number): number {
  const max = sheetHeightPx('lg', stableHeight);
  if (!Number.isFinite(px)) return max;
  return Math.min(Math.max(px, 0), max);
}

/**
 * Where a released drag rests: the nearest of the three heights, or `null`
 * (close) when the panel was pulled below `SHEET_CLOSE_FRACTION` of the
 * stable height.
 */
export function snapSheetHeight(
  px: number,
  stableHeight: number
): TSheetHeight | null {
  if (!Number.isFinite(stableHeight) || stableHeight <= 0) return 'md';
  if (!Number.isFinite(px)) return 'md';
  const fraction = px / stableHeight;
  if (fraction < SHEET_CLOSE_FRACTION) return null;
  let nearest: TSheetHeight = 'md';
  let distance = Number.POSITIVE_INFINITY;
  for (const height of SHEET_HEIGHTS) {
    const gap = Math.abs(SHEET_HEIGHT_FRACTIONS[height] - fraction);
    if (gap < distance) {
      nearest = height;
      distance = gap;
    }
  }
  return nearest;
}
